import React from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, COLORS, formatDate, formatMonth, formatNumber, tooltipStyle } from '../types';

interface OverviewTabProps {
  period: PeriodConfig;
}

const ENDPOINTS = [
  'overview', 'videos-overview', 'signups-daily', 'signups-hourly',
  'recent-signups', 'engagement-daily',
  'cumulative-growth', 'platforms', 'reg-types', 'signups-monthly',
];

const OverviewTab: React.FC<OverviewTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(ENDPOINTS, period);

  const overview = data['overview'];
  const videosOverview = data['videos-overview'];

  if (loading) return <LoadingSpinner />;

  const engagement = overview?.engagement || {};

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Active Users" value={overview?.totalUsers || 0} subtitle="Active accounts only" trend={overview?.growthRate} />
        <StatCard title="Today's Signups" value={overview?.todaySignups || 0} subtitle={`vs ${overview?.yesterdaySignups || 0} yesterday`} />
        <StatCard title="This Week" value={overview?.weekSignups || 0} subtitle="Last 7 days" />
        <StatCard title="This Month" value={overview?.monthSignups || 0} subtitle="Last 30 days" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Active Videos" value={videosOverview?.totalVideos || 0} subtitle={`${videosOverview?.todayVideos || 0} today`} />
        <StatCard title="Verified Users" value={overview?.verifiedUsers || 0} subtitle={`${overview ? ((overview.verifiedUsers / overview.totalUsers) * 100).toFixed(1) : 0}% of total`} />
        <StatCard title="Unique Countries" value={overview?.uniqueCountries || 0} subtitle="Active user locations" />
        <StatCard title="Total DMs" value={overview?.totalDMs || 0} subtitle="Direct messages" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Watches (30d)" value={engagement.watches || 0} />
        <StatCard title="Likes (30d)" value={engagement.likes || 0} />
        <StatCard title="Comments (30d)" value={engagement.comments || 0} />
        <StatCard title="Shares (30d)" value={engagement.shares || 0} />
        <StatCard title="Saves (30d)" value={engagement.saves || 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Daily Signups (Last 90 Days)">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={(data['signups-daily'] || []).map((d: any) => ({ ...d, date: formatDate(d.date) }))}>
              <defs>
                <linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF9C15" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#F42026" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} interval="preserveStartEnd" />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="count" stroke="#FF9C15" fill="url(#signupGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Cumulative User Growth">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={(data['cumulative-growth'] || []).map((d: any) => ({ ...d, label: formatMonth(d.year, d.month) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => formatNumber(v)} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => [formatNumber(v), 'Total Users']} />
              <Line type="monotone" dataKey="totalUsers" stroke="#F42026" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Platform Distribution">
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={data['platforms'] || []} dataKey="count" nameKey="platform" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {(data['platforms'] || []).map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Registration Source" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={data['reg-types'] || []} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={80} innerRadius={40} label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {(data['reg-types'] || []).map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard title="Monthly Signups (from Jul 2025)">
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={(data['signups-monthly'] || []).map((d: any) => ({ ...d, label: formatMonth(d.year, d.month) }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="count" fill="#FF9C15" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
};

export default OverviewTab;
